// src/components/ProjectsCarousel.tsx
import React, { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import { Swiper as SwiperType } from "swiper";

type Project = { name: string; location: string; image: string };
type Props    = { projects: Project[] };

const ProjectsCarousel: React.FC<Props> = ({ projects }) => {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);

  return (
    <section className="bg-white pt-16 pb-12 px-6 md:px-20 relative">
      {/* ---------- heading ---------- */}
      <header className="mb-[40px] flex flex-col justify-center items-center">
        <p className="font-inter font-semibold text-[22px] bg-gradient-to-t from-[#46C1BF] to-[#46C1BF] bg-clip-text  text-transparent mb-4">
          Our Projects //
        </p>
        <h2 className="font-utility font-medium text-[#011621] leading-[120%] tracking-[-0.01em] text-[30px] sm:text-[54px]">
          Where Vision 
        </h2>
        <h2 className="font-utility font-medium text-[#011621] leading-[120%] tracking-[-0.01em] text-[30px] sm:text-[54px]">
        Becomes Reality
        </h2>
      </header>

      {/* ---------- slider ---------- */}
      <div className="relative">
        {/* ========== CUSTOM ARROWS (visible on < md) ========== */}
        <button
          ref={prevRef}
          className="
            md:hidden
            absolute z-10 left-1 !top-[40%] -translate-y-1/2
            w-11 h-11 flex items-center justify-center
            swiper-button-prev
          "
          aria-label="Previous slide"
        >
          <svg
            width="28" height="14" viewBox="0 0 28 14"
            fill="none"
            stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
            className="pointer-events-none absolute transform rotate-90"
            style={{ top: "9.5px", left: "17px" }}
          >
            <polyline points="26 1 13 13 0 1" />
          </svg>
        </button>

        <button
          ref={nextRef}
          className="
            md:hidden
            absolute z-10 !right-10 !top-[40%] -translate-y-1/2
            w-11 h-11 flex items-center justify-center
            swiper-button-next
          "
          aria-label="Next slide"
        >
          <svg
            width="28" height="14" viewBox="0 0 28 14"
            fill="none"
            stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
            className="pointer-events-none absolute transform -rotate-90"
            style={{ top: "9.5px", left: "17px" }}
          >
            <polyline points="2 1 15 13 28 1" />
          </svg>
        </button>

        <Swiper
          modules={[Navigation]}
          navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
          initialSlide={1}
          onInit={(swiper: SwiperType) => {
            swiperRef.current = swiper;
            // @ts-ignore
            swiper.params.navigation.prevEl = prevRef.current;
            // @ts-ignore
            swiper.params.navigation.nextEl = nextRef.current;
            swiper.navigation.init();
            swiper.navigation.update();
            setActiveIndex(swiper.realIndex);
          }}
          onSlideChange={(swiper: SwiperType) => setActiveIndex(swiper.realIndex)}
          centeredSlides
          slidesPerView="auto"
          spaceBetween={11}
          breakpoints={{ 600: { centeredSlides: false, spaceBetween: 32 } }}
        >
          {projects.map((p, idx) => {
            const isPrev = idx === activeIndex - 1 || (activeIndex === 0 && idx === projects.length - 1);
            const isNext = idx === activeIndex + 1 || (activeIndex === projects.length - 1 && idx === 0);

            return (
              <SwiperSlide
                key={p.name}
                style={{ width: 204 }}
                className="
                  flex flex-col sm:!w-[326px]
                  transition-all duration-300
                  origin-top
                  [&:not(.swiper-slide-active)]:opacity-75
                  [&:not(.swiper-slide-active)]:scale-[0.94]
                "
              >
                <div className="relative w-full aspect-[341/512] [clip-path:polygon(0_0,100%_0,100%_90%,90%_100%,0_100%)]">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width={341}
                    height={512}
                  />

                  {/* Gradient overlay on left/right neighbors */}
                  {(isPrev || isNext) && (
                    <button
                      type="button"
                      className={`
                        absolute inset-0 z-10 cursor-pointer
                        transition-opacity duration-300
                        ${isPrev
                          ? 'bg-gradient-to-b from-[#011621]/0 to-[#011621]'
                          : 'bg-gradient-to-b from-[#011621]/0 to-[#011621]'
                        }
                      `}
                      onClick={() => swiperRef.current?.slideTo(idx)}
                      aria-label={`Go to ${p.name}`}
                    />
                  )}

                  {/* Swiper fallback gradient for keyboard/nav */}
                  <div
                    className={`
                      pointer-events-none absolute inset-0
                      transition-opacity duration-300 opacity-0
                      swiper-slide-next:opacity-100 swiper-slide-prev:opacity-100
                      ${isPrev
                        ? 'bg-gradient-to-b from-[#011621]/0 to-[#011621]'
                        : 'bg-gradient-to-b from-[#011621]/0 to-[#011621]'
                      }
                    `}
                  />
                </div>

                <div className="mt-6">
                  <h3 className="text-[20px] sm:text-[24px] font-medium text-[#033652]">
                    {p.name}
                  </h3>
                  <p className="text-[18px] sm:text-[20px] text-[#4F7286]">
                    {p.location}
                  </p>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </section>
  );
};

export default ProjectsCarousel;
